import { FC, useEffect, useMemo, useState } from 'react'
import { Box, IconButton, TextField, Typography } from '@mui/material'
import toast from 'react-hot-toast'
import { isAxiosError } from 'lib/axios'
import { CloseChipIcon } from 'ui/components/svg'
import { ThemeButton } from 'ui/components/buttons'
import SelectV2 from 'ui/components/form-components-v2/select-v2/SelectV2'
import { DEFAULT_MENU_PROPS } from 'ui/components/form-components-v2/select-v2/selectV2.configs'
import { HotToast } from 'ui/components/toast'
import OnyxiaTooltip from 'ui/components/tooltip/OnyxiaTooltip'
import { closeModal, modalPropsSelector } from 'store/modals/modalSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { JiraAssignableUsers, JiraCreateField, JiraOption, JiraTicketProject } from 'models'
import { ApplicationsServiceV4 } from 'services/v4/applications-v4.service'
import JiraFieldInput from './JiraFieldInput'
import { buildJiraFields, initialValueOf, isEmptyValue, isWizardField, prefillTargetDates } from './jiraFields'
import {
  RecordsTable,
  StepActions,
  StepPanel,
  StepsList,
  TicketModalBody,
  TicketModalHeader,
  TicketModalRoot
} from './createTicket.styles'

const STEPS = ['Destination', 'Ticket Details', 'Additional Info', 'Summary']

const MENU_PROPS = { ...DEFAULT_MENU_PROPS, sx: { zIndex: 10003 } }

const SUMMARY_MAX_LENGTH = 255

/* Figma's snackbar for this flow: a deeper green than the app-wide success toast, with light text. */
const TICKET_CREATED_TOAST_STYLE = { background: '#4F6B31', color: '#E9E0E4' }

type Loadable<T> = { status: 'idle' | 'loading' | 'ready' | 'failed'; data: T }

const idle = <T,>(data: T): Loadable<T> => ({ status: 'idle', data })

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 8.5L7 12.5L13 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const RemoveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

/* FastAPI answers 422 with a list of `{loc, msg}`; other failures carry a `detail` or `message` string. */
const errorMessageOf = (e: unknown): string => {
  if (isAxiosError(e)) {
    const data: any = e.response?.data
    if (Array.isArray(data?.detail)) return data.detail.map((d: any) => d?.msg).filter(Boolean).join(' ')
    if (typeof data?.detail === 'string') return data.detail
    if (typeof data?.message === 'string') return data.message
  }
  return 'The ticket could not be created. Please try again.'
}

/* Issue types Jira gives no id are sent by name, the way the backend matches them. */
const issueTypeValue = (t: JiraOption) => t.id ?? t.name

const CreateTicket: FC = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const props = modalProps.props?.createTicketProps
  const applications = useMemo(() => props?.applications ?? [], [props?.applications])
  const targetDates = props?.targetDates

  const [step, setStep] = useState(0)
  const [applicationId, setApplicationId] = useState(applications.length === 1 ? applications[0].id : '')
  const [projectKey, setProjectKey] = useState('')
  const [issueTypeId, setIssueTypeId] = useState('')
  const [projects, setProjects] = useState<Loadable<JiraTicketProject[]>>(idle([]))
  const [issueTypes, setIssueTypes] = useState<Loadable<JiraOption[]>>(idle([]))
  const [fields, setFields] = useState<Loadable<JiraCreateField[]>>(idle([]))
  const [users, setUsers] = useState<JiraAssignableUsers | null>(null)
  const [values, setValues] = useState<Record<string, any>>({})
  const [summary, setSummary] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  /* Rows taken out on the Summary step, by position in the selection; the backend writes the description from the ids sent. */
  const [removedRows, setRemovedRows] = useState<number[]>([])

  useEffect(() => {
    setProjects(idle([]))
    if (!applicationId) return
    let cancelled = false
    setProjects({ status: 'loading', data: [] })
    ApplicationsServiceV4.listJiraProjects(applicationId)
      .then((data) => !cancelled && setProjects({ status: 'ready', data }))
      .catch(() => !cancelled && setProjects({ status: 'failed', data: [] }))
    return () => {
      cancelled = true
    }
  }, [applicationId])

  useEffect(() => {
    setIssueTypes(idle([]))
    setUsers(null)
    if (!applicationId || !projectKey) return
    let cancelled = false
    setIssueTypes({ status: 'loading', data: [] })
    ApplicationsServiceV4.listJiraIssueTypes(applicationId, projectKey)
      .then((data) => !cancelled && setIssueTypes({ status: 'ready', data: data?.values ?? [] }))
      .catch(() => !cancelled && setIssueTypes({ status: 'failed', data: [] }))
    ApplicationsServiceV4.listJiraUsers(applicationId, projectKey)
      .then((data) => !cancelled && setUsers(data))
      .catch(() => !cancelled && setUsers({ values: [], warning: 'Assignable users could not be loaded.' }))
    return () => {
      cancelled = true
    }
  }, [applicationId, projectKey])

  // A new destination brings its own form, so every value entered for the previous one is dropped.
  useEffect(() => {
    setFields(idle([]))
    setValues({})
    setSummary('')
    setNotes('')
    if (!applicationId || !projectKey || !issueTypeId) return
    let cancelled = false
    setFields({ status: 'loading', data: [] })
    ApplicationsServiceV4.listJiraFields(applicationId, projectKey, issueTypeId)
      .then((data) => {
        if (cancelled) return
        setFields({ status: 'ready', data })
        const initial = data
          .filter(isWizardField)
          .reduce<Record<string, any>>((acc, f) => ({ ...acc, [f.id]: initialValueOf(f) }), {})
        setValues(prefillTargetDates(data, initial, targetDates))
      })
      .catch(() => !cancelled && setFields({ status: 'failed', data: [] }))
    return () => {
      cancelled = true
    }
  }, [applicationId, projectKey, issueTypeId, targetDates])

  const wizardFields = useMemo(() => fields.data.filter(isWizardField), [fields.data])
  const requiredFields = useMemo(() => wizardFields.filter((f) => f.required), [wizardFields])
  const optionalFields = useMemo(() => wizardFields.filter((f) => !f.required), [wizardFields])

  const destinationComplete = Boolean(applicationId && projectKey && issueTypeId) && fields.status === 'ready'
  const summaryValid = Boolean(summary.trim()) && summary.length <= SUMMARY_MAX_LENGTH
  const detailsComplete = summaryValid && requiredFields.every((f) => !isEmptyValue(values[f.id]))

  const canAdvance = [destinationComplete, detailsComplete, true, detailsComplete][step]

  if (!props) return null

  const { cpiSlug, cpiDisplayId, recordIds, records, recordColumns, onSuccess } = props

  const keptRows = records.map((_, i) => i).filter((i) => !removedRows.includes(i))

  const handleClose = () => {
    if (submitting) return
    dispatch(closeModal())
  }

  const handleCreate = async () => {
    setSubmitting(true)
    setServerError(null)
    try {
      const created = await ApplicationsServiceV4.createJiraTicket(applicationId, {
        metric_slug: cpiSlug,
        project_key: projectKey,
        issue_type_id: issueTypeId,
        summary: summary.trim(),
        description: notes.trim() || null,
        fields: buildJiraFields(fields.data, values),
        records: { ids: recordIds.filter((_, i) => !removedRows.includes(i)) }
      })
      dispatch(closeModal())
      onSuccess?.()
      toast.success(
        () => (
          <HotToast
            text="Ticket created successfully"
            description={
              created.url ? (
                <a href={created.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                  Open {created.key} in Jira
                </a>
              ) : (
                created.key
              )
            }
          />
        ),
        { style: TICKET_CREATED_TOAST_STYLE }
      )
    } catch (e) {
      setServerError(errorMessageOf(e))
    } finally {
      setSubmitting(false)
    }
  }

  const handleNext = () => {
    if (step === STEPS.length - 1) {
      handleCreate()
      return
    }
    setServerError(null)
    setStep(step + 1)
  }

  const label = (text: string, required = false) => (
    <Typography className="field-label">
      {text}
      {required && <span className="required">*</span>}
    </Typography>
  )

  const renderFieldList = (list: JiraCreateField[]) =>
    list.map((f) => (
      <Box className="field" key={f.id}>
        {label(f.label, f.required)}
        <JiraFieldInput
          field={f}
          value={values[f.id]}
          users={users}
          onChange={(next) => setValues((prev) => ({ ...prev, [f.id]: next }))}
        />
      </Box>
    ))

  const summaryInput = (
    <Box className="field">
      {label('Summary', true)}
      <TextField
        fullWidth
        placeholder="Enter Summary"
        value={summary}
        inputProps={{ maxLength: SUMMARY_MAX_LENGTH }}
        onChange={(e) => setSummary(e.target.value)}
      />
    </Box>
  )

  const destinationStep = (
    <Box className="fields">
      <Box className="field">
        {label('Integration', true)}
        <SelectV2
          MenuProps={MENU_PROPS}
          id="ticket-integration"
          label=""
          className={applicationId ? undefined : 'is-empty'}
          placeholder="Select Integration"
          value={applicationId}
          options={applications.map((a) => ({ label: a.display_name, value: a.id }))}
          onChange={(e) => {
            setApplicationId(String(e.target.value))
            setProjectKey('')
            setIssueTypeId('')
          }}
        />
      </Box>
      <Box className="field">
        {label('Project', true)}
        <SelectV2
          MenuProps={MENU_PROPS}
          id="ticket-project"
          label=""
          className={projectKey ? undefined : 'is-empty'}
          placeholder={projects.status === 'loading' ? 'Loading projects...' : 'Select Project'}
          searchable
          disabled={!applicationId || projects.status === 'loading'}
          value={projectKey}
          options={projects.data.map((p) => ({ label: p.name, value: p.key }))}
          emptyMessage={projects.status === 'failed' ? 'Projects could not be loaded' : 'No projects available'}
          onChange={(e) => {
            setProjectKey(String(e.target.value))
            setIssueTypeId('')
          }}
        />
      </Box>
      <Box className="field">
        {label('Issue Type', true)}
        <SelectV2
          MenuProps={MENU_PROPS}
          id="ticket-issue-type"
          label=""
          className={issueTypeId ? undefined : 'is-empty'}
          placeholder={issueTypes.status === 'loading' ? 'Loading issue types...' : 'Select Issue Type'}
          disabled={!projectKey || issueTypes.status === 'loading'}
          value={issueTypeId}
          options={issueTypes.data.map((t) => ({ label: t.name, value: issueTypeValue(t) }))}
          emptyMessage={issueTypes.status === 'failed' ? 'Issue types could not be loaded' : 'No issue types available'}
          onChange={(e) => setIssueTypeId(String(e.target.value))}
        />
      </Box>
      {fields.status === 'loading' && <Typography className="loading-message">Loading ticket fields...</Typography>}
      {fields.status === 'failed' && (
        <Typography className="empty-message">
          The fields for this project and issue type could not be loaded. Choose another or try again later.
        </Typography>
      )}
    </Box>
  )

  const detailsStep = (
    <Box className="fields">
      {summaryInput}
      {renderFieldList(requiredFields)}
    </Box>
  )

  const additionalStep = (
    <Box className="fields">
      {optionalFields.length ? (
        renderFieldList(optionalFields)
      ) : (
        <Typography className="empty-message">Jira has no optional fields for this issue type.</Typography>
      )}
    </Box>
  )

  const gridColumns = `repeat(${Math.max(recordColumns.length, 1)}, minmax(0, 1fr)) 32px`

  const summaryStep = (
    <>
      <Typography className="section-description">
        Review the ticket title and description before creating it in Jira.
      </Typography>
      <Box className="fields">
        {summaryInput}
        <Box className="field">
          {label('Notes')}
          <TextField
            fullWidth
            multiline
            minRows={4}
            placeholder="Add context, steps or instructions for the assignee..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
        <Box className="field">
          {label('Description')}
          <RecordsTable style={{ ['--records-columns' as any]: gridColumns }}>
            <Box className="records-row records-header">
              {recordColumns.map((c) => (
                <span key={c.field}>
                  <span className="records-header__label">{c.headerName}</span>
                  <span className="records-header__icon" aria-hidden="true" />
                </span>
              ))}
              <span />
            </Box>
            {keptRows.map((i) => (
              <Box className="records-row" key={recordIds[i] ?? i}>
                {recordColumns.map((c) => {
                  const cell = records[i]?.[c.field]
                  return <span key={c.field}>{cell == null || cell === '' ? '—' : String(cell)}</span>
                })}
                {/* A ticket keeps at least one record. */}
                {keptRows.length > 1 ? (
                  <OnyxiaTooltip title="Remove from ticket" placement="top" PopperProps={{ sx: { zIndex: 10003 } }}>
                    <IconButton
                      className="records-row__remove"
                      size="small"
                      aria-label="Remove from ticket"
                      onClick={() => setRemovedRows((current) => [...current, i])}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </OnyxiaTooltip>
                ) : (
                  <span />
                )}
              </Box>
            ))}
          </RecordsTable>
        </Box>
      </Box>
    </>
  )

  const stepContent = [destinationStep, detailsStep, additionalStep, summaryStep][step]

  return (
    <TicketModalRoot>
      <TicketModalHeader>
        <Box className="title-group">
          <Typography className="title">Create Ticket</Typography>
          <Box className="cpi-chip">{cpiDisplayId}</Box>
        </Box>
        <IconButton className="close-button" onClick={handleClose} disabled={submitting} aria-label="Close">
          <CloseChipIcon />
        </IconButton>
      </TicketModalHeader>

      <TicketModalBody>
        <StepsList>
          {STEPS.map((name, i) => (
            <Box key={name} className={`step${i === step ? ' active' : ''}${i < step ? ' done' : ''}`}>
              <Box className="step-icon">{i < step ? <CheckIcon /> : i + 1}</Box>
              <Typography className="step-label">{name}</Typography>
            </Box>
          ))}
        </StepsList>

        <StepPanel>
          <Box className="step-content">
            <Typography className="section-title">{STEPS[step]}</Typography>
            {stepContent}
          </Box>
          <StepActions>
            {serverError && <Typography className="server-error">{serverError}</Typography>}
            <ThemeButton
              themevariant="secondary"
              shape="rectangular"
              type="button"
              disabled={submitting}
              onClick={step === 0 ? handleClose : () => setStep(step - 1)}
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </ThemeButton>
            <ThemeButton
              themevariant="primary"
              shape="rectangular"
              type="button"
              disabled={!canAdvance || submitting}
              onClick={handleNext}
            >
              {step === STEPS.length - 1 ? (submitting ? 'Creating...' : 'Create') : 'Next'}
            </ThemeButton>
          </StepActions>
        </StepPanel>
      </TicketModalBody>
    </TicketModalRoot>
  )
}

export default CreateTicket
